import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Button, Container, Form, Input, Label, FormGroup, FormText } from 'reactstrap';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { useNavigate, Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { forgotPasswordActions } from '../../../../core/forgotPassword/actions';
import LoadingIndicator from '../../../components/loading-indicator';

const animationClass = {
    appearActive: 'animate__animated animate__slideInRight',
    enter: 'animate__animated',
    enterActive: 'animate__animated animate__slideInRight',
    exit: 'animate__animated',
    exitActive: 'animate__animated animate__slideOutLeft',
}

const ForgotPassword = ({
    sendVerificationCode,
    verifyCode,
    resetState,
    forgotPassword
}) => {
    const navigate = useNavigate();
    const nodeRef = useRef(null);
    const [formState, setFormState] = useState({
        email: '',
        code: '',
        emailVerified: false,
        codeSent: false
    });

    const [errors, setErrors] = useState({
        email: null,
        code: null
    });

    useEffect(() => {
        resetState();
    }, [resetState]);

    useEffect(() => {
        if (forgotPassword.isPending) return;

        if (forgotPassword.action === forgotPasswordActions.FORGOT_PASSWORD) {
            if (forgotPassword.success && forgotPassword.data) {
                setFormState(prev => ({
                    ...prev,
                    emailVerified: true,
                    codeSent: true
                }));
                setErrors(prev => ({ ...prev, email: null }));
            } else {
                setErrors(prev => ({
                    ...prev,
                    email: forgotPassword.message || ''
                }));
            }
        }
    }, [forgotPassword]);

    // code verification
    useEffect(() => {
        if (forgotPassword.isPending) return;

        if (forgotPassword.action === forgotPasswordActions.VERIFY_PASSWORD_CHANGE_CODE) {
            if (forgotPassword.success) {
                navigate('/reset-password');
            } else {
                setErrors(prev => ({
                    ...prev,
                    code: forgotPassword.message || 'Verification code not valid.'
                }));
            }
        }
    }, [forgotPassword]);

    const handleInputChange = ({ target }) => {
        const { name, value } = target;
        setFormState(prev => ({
            ...prev,
            [name]: value
        }));

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const handleSendCode = (e) => {
        e.preventDefault();

        sendVerificationCode({
            email: formState.email
        });
    };

    const handleVerifyCode = (e) => {
        e.preventDefault();

        if (!formState.code) {
            setErrors(prev => ({ ...prev, code: 'Verification code is required' }));
            return;
        }

        verifyCode({ 
            id: forgotPassword.data?.id,
            code: formState.code
        });
    };

    return (
        <>
            {forgotPassword.isPending && <LoadingIndicator />}

            <Container fluid="xl" className='minvh-100 overflow-hidden position-relative px-0 px-md-3'>
                <TransitionGroup component={null}>
                    <CSSTransition nodeRef={nodeRef} classNames={animationClass} timeout={{ enter: 1000, exit: 1000 }} key='confirm' appear>
                        <div ref={nodeRef} className='d-flex flex-column align-items-center minvh-100'>
                            <div className="forgot-password-page bg-white p-4 mt-0 my-md-auto">
                                <div className='mb-3 pt-md-3 d-flex align-items-center'>
                                    <Button color='link' className="text-body p-0 mr-3 mb-1" onClick={() => { resetState(); navigate('/welcome') }}>
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" width="24" height="20" viewBox="0 0 24 20">
                                            <path d="M24,10c0,0.3-0.1,0.5-0.3,0.7C23.5,10.9,23.3,11,23,11H3.4l7.3,7.3c0.1,0.1,0.2,0.2,0.2,0.3
                                            C11,18.7,11,18.9,11,19c0,0.1,0,0.3-0.1,0.4c-0.1,0.1-0.1,0.2-0.2,0.3c-0.1,0.1-0.2,0.2-0.3,0.2C10.3,20,10.1,20,10,20
                                            c-0.1,0-0.3,0-0.4-0.1c-0.1-0.1-0.2-0.1-0.3-0.2l-9-9c-0.1-0.1-0.2-0.2-0.2-0.3C0,10.3,0,10.1,0,10c0-0.1,0-0.3,0.1-0.4
                                            c0.1-0.1,0.1-0.2,0.2-0.3l9-9C9.5,0.1,9.7,0,10,0c0.3,0,0.5,0.1,0.7,0.3C10.9,0.5,11,0.7,11,1c0,0.3-0.1,0.5-0.3,0.7L3.4,9H23
                                            c0.3,0,0.5,0.1,0.7,0.3C23.9,9.5,24,9.7,24,10z"/>
                                        </svg>
                                    </Button>
                                    <h1 className='libre-caslon-text-regular h3-md h4 mb-0'>Forgot Password?</h1>
                                </div>
                                <p className='mb-3'>Please enter your user email bellow and we’ll send you a verification code to change your password</p>

                                <Form>
                                    {/* Campo de email */}
                                    <FormGroup>
                                        <Label for="email" className={`${formState.emailVerified ? 'text-green' : ''} ${(errors.email || forgotPassword.type === 'Not Found') ? 'text-danger' : ''}`}>Email Address</Label>
                                        <Input
                                            type="email"
                                            name="email"
                                            id="email"
                                            placeholder="username@mail.com"
                                            className={formState.emailVerified ? 'text-green bg-white' : ''}
                                            value={formState.email}
                                            onChange={handleInputChange}
                                            disabled={formState.emailVerified}
                                            valid={formState.emailVerified}
                                            invalid={errors.email || forgotPassword.type === 'Not Found'}
                                        />
                                        {errors.email && <FormText color="danger">{errors.email}</FormText>}

                                        {/* Mensajes específicos de error del backend */}
                                        {forgotPassword.type === 'Not Found' && !forgotPassword.success && (
                                            <>
                                                {forgotPassword.belong_to_any_account ? (
                                                    <Fragment>
                                                        <FormText color='danger'>This user does not belong to this Account.</FormText>
                                                        <div className='text-muted pt-3'>
                                                            <p>Please <Link to="/account-information-sent" className="text-danger font-weight-semibold">click here </Link> to recieve an email with the correct account information.</p>
                                                            <p>If you experience any issues <Link to="/contact-us" className="text-danger font-weight-semibold"> contact us here. </Link></p>
                                                        </div>
                                                    </Fragment>
                                                ) : (
                                                    <Fragment>
                                                        <FormText color='danger'>This email address does not exist in our system.</FormText>
                                                        <div className='text-muted pt-3'>
                                                            <p>Please check your email or <Link to={'/signup'} className="text-danger font-weight-semibold">create a new account.</Link></p>
                                                            <p>If you experience any issues <Link to="/contact-us" className="text-danger font-weight-semibold"> contact us here. </Link></p>
                                                        </div>
                                                    </Fragment>
                                                )}
                                            </>
                                        )}
                                    </FormGroup>

                                    {/* Botón para enviar código */}
                                    {!formState.codeSent && (
                                        <Button
                                            color="green-light"
                                            className='py-2 rounded-sm mb-3'
                                            size='sm'
                                            block
                                            onClick={handleSendCode}
                                            disabled={!formState.email || forgotPassword.isPending }
                                        >
                                            Send Code
                                        </Button>
                                    )}

                                    {/* Campo de código (solo visible después de enviar el email) */}
                                    {formState.codeSent && (
                                        <>
                                            <FormGroup>
                                                <Label for="code">Verification Code</Label>
                                                <Input
                                                    type="text"
                                                    name="code"
                                                    id="code"
                                                    placeholder="XXXXXX"
                                                    value={formState.code}
                                                    onChange={handleInputChange}
                                                    invalid={!!errors.code}
                                                />
                                                {errors.code && <FormText color="danger">{errors.code}</FormText>}
                                            </FormGroup>

                                            <Button
                                                color="green-light"
                                                className='py-2 rounded-sm mb-3'
                                                size='sm'
                                                block
                                                onClick={handleVerifyCode}
                                                disabled={!formState.code || forgotPassword.isPending}
                                            >
                                                Confirm
                                            </Button>
                                        </>
                                    )}
                                </Form>
                            </div>
                        </div>
                    </CSSTransition>
                </TransitionGroup>
            </Container>
        </>
    );
};

const mapStateToProps = (state) => ({
    forgotPassword: state.forgotPassword
});

const mapDispatchToProps = {
    sendVerificationCode: forgotPasswordActions.forgotResetPassword,
    verifyCode: forgotPasswordActions.verifyPasswordChangeCode,
    resetState: forgotPasswordActions.resetState
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(ForgotPassword);